// The editor's side of captions (owner, 2026-10-06 — #WA-269649).
//
//   · « Légendes IA » (staff): the model's words are placed by the shared rule
//     (captionLayout.ts) and applied as a NORMAL edit — only `elements` change,
//     so autosave, undo and the history panel treat it like any other edit.
//   · A caption follows its photo: dragged to another page, swapped, or moved by
//     « Régénérer IA » (which re-attaches elements by PAGE NUMBER), the photo
//     takes its words along (captionLayout.followCaptions).
//
// Pure, except `lumaGridOf` which reads pixels from an image already loaded.
// Guard: editorCaptions.test.ts

import {
  captionAlbum, existingPageCaptions, facesOf, followCaptions, placeLineOf, uncaption, unpageCaption, writePageCaptions,
  type CaptionAlbumOpts, type CaptionAlbumResult, type CaptionPage, type CaptionPhoto, type Luma, type LumaSampler, type GapSpec,
} from './captionLayout';

/** The editor's LayoutPage, as far as captions care. */
type EditorPage = { page: number; photos: string[]; elements?: ReadonlyArray<unknown> };
const elementsOf = (p: EditorPage) => (p.elements ?? []) as Array<Record<string, unknown>>;

/** Size and faces of the editor's photos — what the placement avoids. A photo of unknown size is left out. */
export function photoMetaFrom(photos: Array<{ id: string; width?: number | null; height?: number | null; face_data?: unknown }>): Record<string, CaptionPhoto> {
  const out: Record<string, CaptionPhoto> = {};
  for (const p of photos ?? []) {
    const w = Number(p.width ?? 0), h = Number(p.height ?? 0);
    if (!(w > 0 && h > 0)) continue;
    out[p.id] = { id: p.id, width: w, height: h, faces: facesOf(p.face_data) };
  }
  return out;
}

/** The photos to caption, in album order: all pages, or pages from…to. */
export function captionPhotoIds(pages: EditorPage[], range: { from: number; to: number } | null): string[] {
  return pages
    .filter((p) => !range || (Number(p.page) >= range.from && Number(p.page) <= range.to))
    .flatMap((p) => (p.photos ?? []).filter(Boolean).map(String));
}

/**
 * Take ONLY the elements of the placed pages onto the editor's own pages
 * (aligned by index — the placement never adds or removes a page). A page whose
 * elements did not change keeps its identity.
 */
export function mergeElements<T extends EditorPage>(local: T[], placed: ReadonlyArray<{ elements?: ReadonlyArray<unknown> }>): T[] {
  let changed = false;
  const next = local.map((p, i) => {
    const els = placed[i]?.elements ?? [];
    if (els === p.elements || JSON.stringify(els) === JSON.stringify(p.elements ?? [])) return p;
    changed = true;
    return { ...p, elements: els } as T;
  });
  return changed ? next : local;
}

/**
 * Captions follow their photos, on the editor's pages. `positionsByPage` is
 * the editor's slot-position map folded into each page (withSlotPositions), so
 * a moved caption is placed against the photo as it is framed now. Returns the
 * SAME array when nothing had to move.
 */
export function followCaptionsInEditor<T extends EditorPage>(
  local: T[],
  slotPositions: Record<number, unknown>,
  meta: Record<string, CaptionPhoto>,
  gapMm: GapSpec,
): T[] {
  if (!local.some((p) => elementsOf(p).some((e) => typeof e.captionFor === 'string'))) return local;
  const framed = local.map((p) => (slotPositions[Number(p.page)] ? { ...p, slot_positions: slotPositions[Number(p.page)] } : p));
  const followed = followCaptions(framed as unknown as CaptionPage[], meta, { gapMm });
  if (followed === (framed as unknown as CaptionPage[])) return local;
  return mergeElements(local, followed as ReadonlyArray<{ elements?: ReadonlyArray<unknown> }>);
}

/** A photo's light, sampled once: relative luminance (WCAG) on a small grid. */
export interface LumaGrid { w: number; h: number; data: Float32Array }

/** Mean and spread of the grid over a region (the photo's own 0..1 coordinates). */
export function lumaFromGrid(grid: LumaGrid, r: { x0: number; y0: number; x1: number; y1: number }): Luma | null {
  const x0 = Math.max(0, Math.floor(r.x0 * grid.w)), x1 = Math.min(grid.w, Math.max(x0 + 1, Math.ceil(r.x1 * grid.w)));
  const y0 = Math.max(0, Math.floor(r.y0 * grid.h)), y1 = Math.min(grid.h, Math.max(y0 + 1, Math.ceil(r.y1 * grid.h)));
  let n = 0, sum = 0, sq = 0;
  for (let y = y0; y < y1; y += 1) {
    for (let x = x0; x < x1; x += 1) {
      const v = grid.data[y * grid.w + x];
      n += 1; sum += v; sq += v * v;
    }
  }
  if (!n) return null;
  const mean = sum / n;
  return { mean, std: Math.sqrt(Math.max(0, sq / n - mean * mean)) };
}

export function samplerFrom(grids: Record<string, LumaGrid | undefined>): LumaSampler {
  return (photoId, region) => {
    const g = grids[photoId];
    return g ? lumaFromGrid(g, region) : null;
  };
}

/** Read an already-loaded image into a small luminance grid. Null when the browser refuses (CORS). */
export function lumaGridOf(img: CanvasImageSource & { naturalWidth?: number }, w = 32, h = 32): LumaGrid | null {
  try {
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, w, h);
    const px = ctx.getImageData(0, 0, w, h).data;
    const data = new Float32Array(w * h);
    const lin = (c8: number) => { const v = c8 / 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; };
    for (let i = 0; i < w * h; i += 1) {
      data[i] = 0.2126 * lin(px[i * 4]) + 0.7152 * lin(px[i * 4 + 1]) + 0.0722 * lin(px[i * 4 + 2]);
    }
    return { w, h, data };
  } catch {
    return null;
  }
}

/** Why a photo got no caption, for the team, in French. */
export function frenchReason(reason: string, why = ''): string {
  const quoted = /«\s*([^»]+?)\s*»/.exec(why)?.[1];
  switch (reason) {
    case 'face': return 'Chaque emplacement possible couvrirait un visage — à placer à la main.';
    case 'text': return /caption box/.test(why)
      ? 'Le cadre de texte du modèle contient des mots écrits à la main (« Remplacer les textes » est décoché).'
      : 'Un autre texte occupe déjà le haut et le bas de cette photo.';
    case 'template': return 'Page avec un modèle décoratif — à placer à la main.';
    case 'too_long': {
      const cap = /≈ (\d+) characters/.exec(why)?.[1];
      if (cap) return `Trop long pour le cadre de texte du modèle (≈ ${cap} caractères par langue) — raccourcissez.`;
      const n = /too small for (\d+) languages/.exec(why)?.[1];
      if (n) return `Le cadre de texte de ce modèle est trop petit pour ${n} langues — moins de langues, ou à la main.`;
      return 'Le texte est trop long pour cette photo — raccourcissez-le.';
    }
    case 'no_holder': return 'Ce modèle n\'a pas de cadre de texte pour cette photo — rien n\'est posé au hasard.';
    case 'no_room': return 'Pas de place : ni à côté de la photo ni sur elle sans toucher un visage, dans la zone de sécurité (12 mm des bords, 15 mm du dos) — à placer à la main.';
    case 'not_in_album': return 'Cette photo n\'est plus dans l\'album.';
    case 'no_photo_size': return 'Taille de la photo inconnue : impossible d\'éviter les visages.';
    default: break;
  }
  if (/names someone or somewhere/.test(why)) return `L'IA a écrit un nom que le client n'a pas donné${quoted ? ` (« ${quoted} »)` : ''}.`;
  if (/age or a date/.test(why)) return `L'IA a écrit un âge ou une date que le client n'a pas donné${quoted ? ` (« ${quoted} »)` : ''}.`;
  if (/family relation/.test(why)) return `L'IA a écrit un lien de parenté invisible sur la photo${quoted ? ` (« ${quoted} »)` : ''}.`;
  if (/must use (Arabic|Latin) letters/.test(why)) return 'Une ligne n\'est pas dans le bon alphabet.';
  if (/text box/.test(why) && /too long/.test(why)) return 'Une ligne est trop longue pour le cadre de texte du modèle.';
  if (/too long/.test(why)) return 'Une ligne est trop longue pour la photo.';
  if (/no copy of this photo/.test(why)) return 'Pas de copie lisible de cette photo (HEIC pas encore converti ?).';
  if (/model call failed|could not run|daily_cap/.test(why)) return `L'IA n'a pas répondu (${why.slice(0, 80)}).`;
  return why ? `Non écrit : ${why}` : 'Non écrit.';
}

/**
 * A caption a PERSON carries to another page becomes their own text: the tie
 * to its photo is dropped, or the follow pass would put it straight back on
 * the photo's page. A plain text comes back unchanged (same object).
 */
export function detachCaption<T extends Record<string, unknown>>(el: T): T {
  if (!('captionFor' in el) && !('captionSlot' in el) && !('captionLayout' in el)) return el;
  const { captionFor: _f, captionSlot: _s, captionLayout: _l, ...rest } = el as T & { captionFor?: unknown; captionSlot?: unknown; captionLayout?: unknown };
  void _f; void _s; void _l;
  return rest as unknown as T;
}

// ─── THE DIALOG'S DRAFT → THE ALBUM (captions × models, 2026-10-14) ─────────

/** What the « Légendes IA » dialog holds before « Appliquer ». */
export interface CaptionDraft {
  /** The photos the dialog covers (its pages), in album order. */
  scope: string[];
  /** The words shown for each photo: written by the model, edited, or already in the album. */
  texts: Record<string, string>;
  /** What the album already says for each photo (captionLayout.existingCaptions). */
  before: Record<string, string>;
  /** Pages whose words this tool wrote come off (« Retirer »). */
  removedPages: number[];
  /** The « Lieu · date » line for the model's empty Lieu · date holders ('' = none / take off the one this tool wrote). */
  placeLine?: string;
  /**
   * « Une légende pour la page » (owner, 2026-10-15): page → the model box its
   * caption goes in (Sous-titre, Section…), or null = with its first photo (no
   * page box). A page absent here is captioned photo by photo.
   */
  pageMode?: Record<number, string | null>;
  /** The words of each page-mode page (one line per language). */
  pageTexts?: Record<number, string>;
}

/**
 * The album as « Appliquer » would leave it. Only what CHANGED is written
 * again: new or edited words are placed, cleared words and « Retirer » pages
 * come off; untouched captions stay where they are (a person may have moved
 * one). With the person's own font or colour, every caption of the scope is
 * written again in it. Pure.
 */
export function draftToAlbum(
  pages: CaptionPage[],
  draft: CaptionDraft,
  meta: Record<string, CaptionPhoto>,
  opts: CaptionAlbumOpts,
): CaptionAlbumResult & { removed: number; pagePlaced: Array<{ page: number; elementId: string; role: string }>; pageSkipped: Array<{ page: number; why: string }> } {
  const norm = (s: string | undefined) => String(s ?? '').split('\n').map((l) => l.trim()).filter(Boolean).join('\n');
  const removedPages = new Set(draft.removedPages);
  const pageOf = new Map<string, number>();
  for (const p of pages) for (const id of p.photos ?? []) if (id) pageOf.set(String(id), Number(p.page));
  const restyle = Boolean(String(opts.fontFamily ?? '').trim() || String(opts.fontColor ?? '').trim());
  // Page-mode pages: their photos say nothing of their own — the page's words go in its box,
  // or (no page box) with its first photo.
  const mode = draft.pageMode ?? {};
  const inPageMode = (n: number | undefined) => n !== undefined && Object.prototype.hasOwnProperty.call(mode, n);
  const firstOf = new Map<number, string>();
  for (const p of pages) { const f = (p.photos ?? []).find(Boolean); if (f) firstOf.set(Number(p.page), String(f)); }
  const wordsOf = (id: string): string => {
    const n = pageOf.get(id);
    if (!inPageMode(n)) return draft.texts[id] ?? '';
    return mode[n as number] == null && firstOf.get(n as number) === id ? (draft.pageTexts?.[n as number] ?? '') : '';
  };
  const off: string[] = [];
  const requests: Array<{ photoId: string; text: string }> = [];
  for (const id of draft.scope) {
    const now = norm(wordsOf(id));
    const was = norm(draft.before[id]);
    if (removedPages.has(pageOf.get(id) ?? NaN) || !now) { if (was) off.push(id); continue; }
    if (now !== was || restyle) requests.push({ photoId: id, text: now });
  }
  const line = norm(draft.placeLine).replace(/\n/g, ' ');
  const scopePages = [...new Set(draft.scope.map((id) => pageOf.get(id)).filter((n): n is number => n !== undefined))];
  // Every page of the range (a page with no photo has its Lieu · date too).
  const placePages = (opts.placePages ?? scopePages).filter((n) => !removedPages.has(n));
  // Page captions: what changed comes off BEFORE the photos are captioned (a page Légende
  // a page caption filled is free again), and is written after.
  const had = existingPageCaptions(pages);
  const pageClear: number[] = [];
  const pageWrite: Array<{ page: number; elementId: string; text: string }> = [];
  for (const n of scopePages) {
    const box = inPageMode(n) && !removedPages.has(n) ? mode[n] : null;
    const words = box ? norm(draft.pageTexts?.[n]) : '';
    const was = had[n];
    if (!box || !words) { if (was) pageClear.push(n); continue; }
    if (!was || was.elementId !== box || norm(was.text) !== words || restyle) { if (was) pageClear.push(n); pageWrite.push({ page: n, elementId: box, text: words }); }
  }
  // No line: the one this tool wrote comes off the range too.
  const base = uncaption(unpageCaption(pages, pageClear), off, line ? removedPages : new Set([...removedPages, ...placePages]));
  // The line is written again only when it changed, a holder is still empty, or the person restyles.
  const want = new Set(placePages);
  const emptyHolder = pages.some((p) => p.theme && want.has(Number(p.page))
    && (p.elements ?? []).some((e) => e && e.type === 'text' && e.holder === 'lieu_date' && !String(e.text ?? '').trim()));
  const writePlace = Boolean(line) && (line !== placeLineOf(pages, true) || emptyHolder || restyle);
  const r0 = captionAlbum(base, requests, meta, { ...opts, placeLine: writePlace ? line : null, placePages });
  const pw = writePageCaptions(r0.pages, pageWrite, { fontFamily: opts.fontFamily, fontColor: opts.fontColor });
  const r = { ...r0, pages: pw.pages };
  const pagesOff = pageClear.filter((n) => !pageWrite.some((w) => w.page === n)).length;
  // « Lieu · date » lines this tool wrote that come off with a « Retirer » page count as removed too.
  const placesOff = pages.filter((p) => removedPages.has(Number(p.page)))
    .reduce((n, p) => n + (p.elements ?? []).filter((e) => e && e.holder === 'lieu_date' && e.captionPlace).length, 0);
  return { ...r, removed: off.length + placesOff + pagesOff, pagePlaced: pw.placed, pageSkipped: pw.skipped };
}
